import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, KeyRound, Plus, Shield, Trash2, UserX, X } from "lucide-react";
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from "../lib/api.js";
import { Panel } from "../components/Panel.js";
import { useAuth } from "../auth/AuthContext.js";
import { ROLE_LABELS, ROLE_DESCRIPTIONS, PII_DESCRIPTION, type ProgrammeRole } from "./roles.js";

/**
 * Staff account management — docs/03-API-SPEC.md §4, superadmin-only. The backend
 * (`apps/api/src/routes/users.ts`) has covered full CRUD and role assignment since Phase 2; this
 * screen was the missing half.
 *
 * Role assignment is a per-user expandable panel, not a table column per programme — a column grid
 * tops out fast: past a handful of programmes it stops fitting a screen and a new programme means
 * widening every row whether or not a given user has anything to do with it. This scales to any
 * number of programmes because it only ever lists what a user actually has.
 */

interface StaffUser {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;
  isSuperadmin: boolean;
  canViewPii: boolean;
  totpEnabledAt: string | null;
  lastLoginAt: string | null;
  programmeRoles: { programmeId: string; role: ProgrammeRole }[];
}

interface Programme {
  id: string;
  name: string;
}

const RoleGlossary = (): JSX.Element => (
  <details className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
    <summary className="cursor-pointer select-none font-medium text-foreground">
      What can each role do?
    </summary>
    <dl className="mt-2 space-y-1.5">
      {(Object.keys(ROLE_LABELS) as ProgrammeRole[]).map((role) => (
        <div key={role}>
          <dt className="font-medium text-foreground">{ROLE_LABELS[role]}</dt>
          <dd>{ROLE_DESCRIPTIONS[role]}</dd>
        </div>
      ))}
      <div>
        <dt className="font-medium text-foreground">View PII (separate from role)</dt>
        <dd>{PII_DESCRIPTION}</dd>
      </div>
    </dl>
  </details>
);

/** The expandable role-management panel for one user. */
const RoleManager = ({
  user,
  programmes,
  onSetRole,
  onRemoveRole,
}: {
  user: StaffUser;
  programmes: Programme[];
  onSetRole: (programmeId: string, role: ProgrammeRole) => void;
  onRemoveRole: (programmeId: string) => void;
}): JSX.Element => {
  const assignedIds = new Set(user.programmeRoles.map((r) => r.programmeId));
  const unassigned = programmes.filter((p) => !assignedIds.has(p.id));
  const [addProgrammeId, setAddProgrammeId] = useState("");
  const [addRole, setAddRole] = useState<ProgrammeRole>("EDITOR");

  return (
    <div className="space-y-3 border-t bg-muted/20 p-4">
      {user.programmeRoles.length === 0 ? (
        <p className="text-sm text-muted-foreground">No programme access yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {user.programmeRoles.map((role) => {
            const programme = programmes.find((p) => p.id === role.programmeId);
            return (
              <li
                key={role.programmeId}
                className="flex items-center justify-between rounded-md border bg-card px-3 py-2 text-sm"
              >
                <span className="font-medium">{programme?.name ?? role.programmeId}</span>
                <span className="flex items-center gap-2">
                  <select
                    aria-label={`${user.fullName}'s role on ${programme?.name ?? role.programmeId}`}
                    className="rounded-md border px-2 py-1 text-xs"
                    value={role.role}
                    onChange={(event) =>
                      onSetRole(role.programmeId, event.target.value as ProgrammeRole)
                    }
                  >
                    {(Object.keys(ROLE_LABELS) as ProgrammeRole[]).map((option) => (
                      <option key={option} value={option}>
                        {ROLE_LABELS[option]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    title="Remove access to this programme"
                    onClick={() => onRemoveRole(role.programmeId)}
                    className="rounded-md p-1 text-destructive hover:bg-destructive/10"
                  >
                    <X aria-hidden className="h-3.5 w-3.5" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {unassigned.length > 0 && (
        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <label className="flex-1">
            <span className="mb-1 block text-[11px] uppercase text-muted-foreground">
              Add programme
            </span>
            <select
              className="w-full rounded-md border px-2 py-1.5 text-sm"
              value={addProgrammeId}
              onChange={(event) => setAddProgrammeId(event.target.value)}
            >
              <option value="">Select…</option>
              {unassigned.map((programme) => (
                <option key={programme.id} value={programme.id}>
                  {programme.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Role</span>
            <select
              className="rounded-md border px-2 py-1.5 text-sm"
              value={addRole}
              onChange={(event) => setAddRole(event.target.value as ProgrammeRole)}
            >
              {(Object.keys(ROLE_LABELS) as ProgrammeRole[]).map((option) => (
                <option key={option} value={option}>
                  {ROLE_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={!addProgrammeId}
            onClick={() => {
              onSetRole(addProgrammeId, addRole);
              setAddProgrammeId("");
            }}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
          >
            <Plus aria-hidden className="h-3.5 w-3.5" />
            Add
          </button>
        </div>
      )}
    </div>
  );
};

export const UsersPage = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", fullName: "", initialPassword: "" });
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: () => apiGet<{ data: StaffUser[] }>("/users"),
  });

  const programmesQuery = useQuery({
    queryKey: ["programmes"],
    queryFn: () => apiGet<{ data: Programme[] }>("/programmes"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const createUser = useMutation({
    mutationFn: () => apiPost<StaffUser>("/users", { ...form, isSuperadmin: false }),
    onSuccess: async () => {
      await invalidate();
      setCreating(false);
      setForm({ email: "", fullName: "", initialPassword: "" });
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not create user."),
  });

  const toggleActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      apiPatch(`/users/${input.id}`, { isActive: input.isActive }),
    onSuccess: invalidate,
  });

  const toggleViewPii = useMutation({
    mutationFn: (input: { id: string; canViewPii: boolean }) =>
      apiPatch(`/users/${input.id}`, { canViewPii: input.canViewPii }),
    onSuccess: invalidate,
  });

  const deleteUser = useMutation({
    mutationFn: (id: string) => apiDelete(`/users/${id}`),
    onSuccess: invalidate,
  });

  const setRole = useMutation({
    mutationFn: (input: { id: string; programmeId: string; role: ProgrammeRole | null }) =>
      apiPost(`/users/${input.id}/roles`, { programmeId: input.programmeId, role: input.role }),
    onSuccess: invalidate,
  });

  const users = usersQuery.data?.data ?? [];
  const programmes = programmesQuery.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Staff accounts</h1>
          <p className="text-sm text-muted-foreground">
            Access is per programme — expand a row to see or change what someone can do where.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
        >
          <Plus aria-hidden className="h-4 w-4" />
          New user
        </button>
      </div>

      <RoleGlossary />

      {creating && (
        <Panel title="New staff account">
          {error && (
            <p className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              createUser.mutate();
            }}
          >
            <label>
              <span className="mb-1 block text-[11px] uppercase text-muted-foreground">
                Full name
              </span>
              <input
                required
                className="rounded-md border px-2 py-1.5 text-sm"
                value={form.fullName}
                onChange={(event) => setForm((f) => ({ ...f, fullName: event.target.value }))}
              />
            </label>
            <label>
              <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Email</span>
              <input
                required
                type="email"
                className="rounded-md border px-2 py-1.5 text-sm"
                value={form.email}
                onChange={(event) => setForm((f) => ({ ...f, email: event.target.value }))}
              />
            </label>
            <label>
              <span className="mb-1 block text-[11px] uppercase text-muted-foreground">
                Initial password
              </span>
              <input
                required
                type="text"
                minLength={12}
                placeholder="12+ characters — share out of band"
                className="w-56 rounded-md border px-2 py-1.5 text-sm"
                value={form.initialPassword}
                onChange={(event) =>
                  setForm((f) => ({ ...f, initialPassword: event.target.value }))
                }
              />
            </label>
            <button
              type="submit"
              disabled={createUser.isPending}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            >
              {createUser.isPending ? "Creating…" : "Create"}
            </button>
          </form>
          <p className="mt-2 text-[11px] text-muted-foreground">
            No email service is wired up yet, so there is no invite link — share this password with
            them directly. They enrol TOTP on first login. New accounts start with no programme
            access; add it by expanding their row below.
          </p>
        </Panel>
      )}

      <div className="divide-y rounded-lg border bg-card">
        {users.map((user) => (
          <div key={user.id}>
            <div
              className={`flex items-center gap-3 px-3 py-2.5 ${user.isActive ? "" : "opacity-50"}`}
            >
              <button
                type="button"
                onClick={() => setExpanded((current) => (current === user.id ? null : user.id))}
                className="flex flex-1 items-center gap-3 text-left"
              >
                {expanded === user.id ? (
                  <ChevronUp aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 font-medium">
                    {user.isSuperadmin && (
                      <Shield aria-label="Superadmin" className="h-3.5 w-3.5 text-primary" />
                    )}
                    {user.fullName}
                  </span>
                  <span className="block text-xs text-muted-foreground">{user.email}</span>
                </span>
                <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
                  {user.isSuperadmin
                    ? "All programmes"
                    : user.programmeRoles.length === 0
                      ? "No programme access"
                      : `${user.programmeRoles.length} programme${user.programmeRoles.length === 1 ? "" : "s"}`}
                </span>
              </button>

              <button
                type="button"
                disabled={user.id === me?.id}
                onClick={() => toggleActive.mutate({ id: user.id, isActive: !user.isActive })}
                className="rounded-md border px-2 py-1 text-xs hover:bg-muted disabled:opacity-40"
              >
                {user.isActive ? "Active" : "Deactivated"}
              </button>

              <label className="flex items-center gap-1.5 text-xs" title={PII_DESCRIPTION}>
                <input
                  type="checkbox"
                  aria-label={`${user.fullName} can view sensitive data`}
                  checked={user.canViewPii}
                  onChange={(event) =>
                    toggleViewPii.mutate({ id: user.id, canViewPii: event.target.checked })
                  }
                />
                View PII
              </label>

              {user.id !== me?.id && (
                <button
                  type="button"
                  title="Delete account"
                  onClick={() => {
                    if (
                      window.confirm(`Delete ${user.fullName}'s account? This cannot be undone.`)
                    ) {
                      deleteUser.mutate(user.id);
                    }
                  }}
                  className="rounded-md p-1 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {expanded === user.id &&
              (user.isSuperadmin ? (
                <p className="border-t bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                  Superadmin — has every permission on every programme already; per-programme roles
                  don't apply.
                </p>
              ) : (
                <RoleManager
                  user={user}
                  programmes={programmes}
                  onSetRole={(programmeId, role) =>
                    setRole.mutate({ id: user.id, programmeId, role })
                  }
                  onRemoveRole={(programmeId) =>
                    setRole.mutate({ id: user.id, programmeId, role: null })
                  }
                />
              ))}
          </div>
        ))}
      </div>

      {users.length === 0 && !usersQuery.isLoading && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No staff accounts yet.
        </p>
      )}

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <KeyRound aria-hidden className="h-3.5 w-3.5" />
        Superadmin status and password resets are not exposed here yet — ask an existing superadmin
        to run those directly against the API until that screen exists.
        <UserX aria-hidden className="ml-2 h-3.5 w-3.5" />
        Deactivating revokes every active session immediately.
      </p>
    </div>
  );
};
